import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import RegisteredArtistCard from '@/components/RegisteredArtistCard';
import artistApi from '@/lib/artistApi';
import { CalendarClock, ClipboardList, Copy, MapPinned, Search, UsersRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const modes = {
  finder: {
    id: 'finder',
    title: 'Find Event Artists',
    eyebrow: 'Find the right artists',
    endpoint: 'event-finder',
    button: 'Find Event Artists',
    helper: 'Use this when you know the event idea and need KalaManch artist roles, direct matches, discovery filters, keywords, and a message to contact creators.',
    summary: 'Artist roles, city filters, matches, and contact message.',
    icon: Search,
    detailsLabel: 'Event requirement',
    detailsPlaceholder: 'Example: Wedding sangeet in Lucknow, 250 guests, needs singer, tabla player, anchor, lively family vibe, budget range...',
  },
  manager: {
    id: 'manager',
    title: 'AI Event Manager',
    eyebrow: 'Plan the event flow',
    endpoint: 'event-manager',
    button: 'Plan My Event',
    helper: 'Use this when you need lineup sequence, timeline, stage flow, sound-check, promotion plan, cost assumptions, and fallback checklist.',
    summary: 'Lineup, timeline, stage flow, costs, and checklist.',
    icon: CalendarClock,
    detailsLabel: 'Event plan brief',
    detailsPlaceholder: 'Example: Devotional music evening in Vrindavan, 3-hour show, 500 audience, stage sequence, artist needs, sound setup, promotion...',
  },
  team: {
    id: 'team',
    title: 'Creative Teams',
    eyebrow: 'Build reusable teams',
    endpoint: 'creative-team',
    button: 'Build Creative Team',
    helper: 'Use this to design a reusable creator team, like wedding music team, short film crew, devotional event group, folk stage team, or launch content team.',
    summary: 'Reusable roles, responsibilities, and invite templates.',
    icon: UsersRound,
    detailsLabel: 'Team requirement',
    detailsPlaceholder: 'Example: Wedding music team for Jaipur: singer, tabla player, guitarist, anchor, DJ handoff, 4-hour event, family audience, reusable team format...',
  },
  events: {
    id: 'events',
    title: 'Find Events Near Me',
    eyebrow: 'Find public opportunities',
    endpoint: 'events-near-me',
    button: 'Find Events',
    helper: 'Use this when artists want public events, concerts, festivals, cultural programs, and venue leads they can approach for performance opportunities.',
    summary: 'Live events, venue leads, and public opportunity links.',
    icon: MapPinned,
    detailsLabel: 'Event search brief',
    detailsPlaceholder: 'Example: music concerts this month, cultural programs, open mics, college events, folk festivals, devotional events, paid performance leads...',
  },
};

const languageOptions = [
  'English + Hindi',
  'Hindi',
  'Hinglish',
  'Urdu',
  'Punjabi',
  'Bhojpuri',
  'Bengali',
  'Marathi',
  'Gujarati',
  'Tamil',
  'Telugu',
];

const cleanMarkdownText = (text = '') =>
  text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();

const EVENT_ARTIST_TERMS = [
  'singer', 'vocalist', 'bhajan', 'qawwali', 'sufi', 'folk', 'band', 'musician',
  'tabla', 'dholak', 'guitar', 'instrumentalist', 'composer', 'lyricist', 'poet',
  'shayari', 'anchor', 'emcee', 'host', 'dancer', 'choreographer', 'actor',
  'comedian', 'speaker', 'dj', 'photographer', 'videographer', 'painter',
];

const tokenizeMatchText = (...values) => {
  const text = values.filter(Boolean).join(' ').toLowerCase();
  const words = text
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length >= 3);
  const roleTerms = EVENT_ARTIST_TERMS.filter((term) => text.includes(term));
  return Array.from(new Set([...roleTerms, ...words])).slice(0, 30);
};

const rankEventArtist = (artist = {}, tokens = [], city = '') => {
  const searchable = [
    artist.name,
    artist.category,
    artist.subcategory,
    artist.city,
    artist.state,
    artist.bio,
    artist.native_bio,
    artist.native_language,
    artist.native_language_badge,
    artist.smart_fit,
  ].filter(Boolean).join(' ').toLowerCase();
  const cleanCity = city.trim().toLowerCase();
  let score = 0;
  tokens.forEach((token) => {
    if (searchable.includes(token)) score += EVENT_ARTIST_TERMS.includes(token) ? 5 : 2;
  });
  if (cleanCity && [artist.city, artist.state].filter(Boolean).some((value) => String(value).toLowerCase().includes(cleanCity))) score += 8;
  if (artist.allow_collaborator_enquiries !== false) score += 3;
  if (artist.is_verified) score += 3;
  if (artist.is_featured) score += 2;
  return score;
};

const getEventArtistMatches = (artists = [], { eventType, city, details, result }) => {
  const tokens = tokenizeMatchText(eventType, details, result);
  if (tokens.length === 0) return [];
  return artists
    .map((artist) => ({ ...artist, match_score: rankEventArtist(artist, tokens, city) }))
    .filter((artist) => artist.match_score > 0)
    .sort((a, b) => b.match_score - a.match_score)
    .slice(0, 6);
};

const publicEventSourceLabel = (source = '') => {
  const clean = String(source || '').toLowerCase();
  if (clean.includes('google') || clean.includes('serpapi')) return 'Live Event';
  if (clean.includes('eventbrite')) return 'Ticketed Event';
  if (clean.includes('openstreetmap') || clean.includes('osm')) return 'Venue Lead';
  return 'Event Lead';
};

const EventResult = ({ result }) => {
  const technicalPatterns = [
    /^source status/i,
    /temporarily unavailable/i,
    /no live event listings returned/i,
    /no open venue leads/i,
    /^-\s*(google events api|eventbrite api|openstreetmap venues)/i,
  ];
  const lines = result
    .split('\n')
    .filter((line) => !technicalPatterns.some((pattern) => pattern.test(line.trim())));
  return (
    <div className="mt-4 space-y-2 text-gray-700">
      {lines.map((rawLine, index) => {
        const line = rawLine.trim();
        if (!line) return <div key={`blank-${index}`} className="h-2" />;

        const headingMatch = line.match(/^(#{1,6})\s*(.+)$/);
        if (headingMatch) {
          const level = headingMatch[1].length;
          const text = cleanMarkdownText(headingMatch[2]);
          return (
            <h4
              key={`heading-${index}`}
              className={`${level <= 3 ? 'pt-3 text-xl' : 'pt-2 text-base'} font-black text-slate-950`}
            >
              {text}
            </h4>
          );
        }

        const numberedMatch = line.match(/^(\d+)\.\s*(.+)$/);
        if (numberedMatch) {
          return (
            <div key={`number-${index}`} className="grid grid-cols-[2rem_1fr] gap-2 rounded-2xl bg-white/70 px-3 py-2">
              <span className="font-black text-[#FF6F00]">{numberedMatch[1]}.</span>
              <span className="leading-7">{cleanMarkdownText(numberedMatch[2])}</span>
            </div>
          );
        }

        const bulletMatch = line.match(/^[-*]\s*(.+)$/);
        if (bulletMatch) {
          return (
            <div key={`bullet-${index}`} className="flex gap-3 rounded-2xl bg-white/60 px-3 py-2">
              <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#00BFA5]" />
              <span className="leading-7">{cleanMarkdownText(bulletMatch[1])}</span>
            </div>
          );
        }

        return (
          <p key={`line-${index}`} className="leading-7">
            {cleanMarkdownText(line)}
          </p>
        );
      })}
    </div>
  );
};

export default function EventPlanner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { artist, loading: artistLoading } = useArtistAuth();
  const { collaborator, loading: collaboratorLoading } = useCollaboratorAuth();
  const modeParam = searchParams.get('mode');
  const requestedMode = Object.keys(modes).includes(modeParam) ? modeParam : 'finder';
  const [modeId, setModeId] = useState(requestedMode);
  const [organizerName, setOrganizerName] = useState('');
  const [eventType, setEventType] = useState('');
  const [city, setCity] = useState('');
  const [date, setDate] = useState('');
  const [audience, setAudience] = useState('');
  const [budget, setBudget] = useState('');
  const [language, setLanguage] = useState('English + Hindi');
  const [details, setDetails] = useState('');
  const [result, setResult] = useState('');
  const [opportunities, setOpportunities] = useState(null);
  const [artists, setArtists] = useState([]);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedTeams, setSavedTeams] = useState([]);
  const [teamSaveStatus, setTeamSaveStatus] = useState('');
  const [referralStatus, setReferralStatus] = useState('');

  const authLoading = artistLoading || collaboratorLoading;
  const isAuthenticated = Boolean(artist || collaborator);
  const mode = modes[modeId];
  const hasUsage = !usage || (usage.enabled && usage.daily_remaining > 0 && usage.platform_daily_remaining > 0);
  const matchedArtists = useMemo(
    () => modeId === 'finder' && result
      ? getEventArtistMatches(artists, { eventType, city, details, result })
      : [],
    [artists, city, details, eventType, modeId, result]
  );

  useEffect(() => {
    setModeId(requestedMode);
  }, [requestedMode]);

  useEffect(() => {
    if (window.location.hash !== '#planner-form') return;
    window.setTimeout(() => {
      document.getElementById('planner-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  }, [requestedMode]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUsage(null);
      return;
    }

    fetch(`${API}/ai/usage`, { credentials: 'include' })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Could not load AI usage.');
        setUsage(data);
      })
      .catch((err) => setError(err.message || 'Could not load AI usage.'));
  }, [isAuthenticated]);

  useEffect(() => {
    fetch(`${API}/artists/all`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Could not load artists.');
        setArtists(data.artists || []);
      })
      .catch(() => setArtists([]));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setSavedTeams([]);
      return;
    }
    artistApi.get('/creative-teams')
      .then((response) => setSavedTeams(response.data.teams || []))
      .catch(() => setSavedTeams([]));
  }, [isAuthenticated]);

  const preparedDetails = useMemo(() => [
    `Mode: ${mode.title}`,
    `City/State: ${city || 'Not specified'}`,
    `Date: ${date || 'Not specified'}`,
    `Expected audience: ${audience || 'Not specified'}`,
    `Budget range: ${budget || 'Not specified'}`,
    '',
    details,
  ].join('\n'), [audience, budget, city, date, details, mode.title]);

  const chooseMode = (nextMode) => {
    setModeId(nextMode);
    setSearchParams({ mode: nextMode }, { replace: true });
    setDetails('');
    window.setTimeout(() => {
      document.getElementById('planner-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    setError('');
    setResult('');
    setOpportunities(null);
    setCopied(false);
  };

  const generate = async () => {
    if (!isAuthenticated) {
      setError('Please login as a registered artist or collaborator to use Event Planner.');
      return;
    }
    if (!hasUsage) {
      setError("Today's free AI limit is finished. Please try again tomorrow.");
      return;
    }
    if (preparedDetails.trim().length < 20) {
      setError('Please add event city, audience, budget, or a few more details.');
      return;
    }

    setLoading(true);
    setError('');
    setCopied(false);
    setOpportunities(null);
    try {
      const response = await fetch(`${API}/ai/${mode.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          artistName: organizerName || artist?.name || collaborator?.organization_name || 'KalaManch organizer',
          artForm: eventType || 'Creative event',
          tone: 'Professional',
          language,
          details: preparedDetails,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Event Planner could not generate this.');
      setResult(data.result || '');
      setOpportunities(data.opportunities || null);
      if (data.usage) setUsage(data.usage);
    } catch (err) {
      setError(err.message || 'Event Planner could not complete this request.');
    } finally {
      setLoading(false);
    }
  };

  const copyResult = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result);
    setCopied(true);
  };

  const saveCreativeTeam = async () => {
    if (!result || modeId !== 'team') return;
    setTeamSaveStatus('Saving team...');
    try {
      const response = await artistApi.post('/creative-teams', {
        title: eventType || 'Creative Team',
        city,
        event_type: eventType,
        result,
        roles: tokenizeMatchText(eventType, details, result).filter((token) => EVENT_ARTIST_TERMS.includes(token)),
        source: 'event_planner',
      });
      setSavedTeams((current) => [response.data.team, ...current.filter((team) => team.id !== response.data.team.id)]);
      setTeamSaveStatus('Creative team saved.');
    } catch (err) {
      setTeamSaveStatus(err.response?.data?.detail || 'Could not save creative team.');
    }
  };

  const referArtist = async (match) => {
    setReferralStatus(`Recording referral for ${match.name || 'artist'}...`);
    try {
      await artistApi.post('/artist-referrals', {
        target_id: match.id,
        target_role: 'artist',
        context: `${mode.title}: ${eventType || 'Creative event'}${city ? ` in ${city}` : ''}`,
        note: `Recommended from Event Planner match for ${eventType || 'creative event'}.`,
        source: 'event_planner',
      });
      setReferralStatus(`Referral recorded for ${match.name || 'artist'}.`);
    } catch (err) {
      setReferralStatus(err.response?.data?.detail || 'Could not record referral.');
    }
  };

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28 pb-16">
      <SEO
        title="KalaManch Event Planner"
        description="Use KalaManch Find Event Artists and AI Event Manager to find artists, plan event lineups, timelines, stage sequence, and promotion."
        keywords="AI event planner India, find artists for event, event lineup planner, KalaManch event manager"
      />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2.25rem] border border-orange-100 bg-white shadow-xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr]">
            <div className="bg-gradient-to-br from-slate-950 via-[#25104F] to-[#FF6F00] p-6 text-white sm:p-8">
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-orange-200">KalaManch Event Planner</p>
              <h1 className="mt-4 text-4xl font-black leading-tight sm:text-5xl">Find artists and plan creative events</h1>
              <p className="mt-5 max-w-xl text-base leading-7 text-white/82">
                Separate from content writing tools. This page is for organizers, collaborators, and artists planning real shows, workshops, launches, sangeet nights, devotional evenings, cultural programs, creator events, and reusable creative teams.
              </p>
              <div className="mt-6 rounded-2xl border border-white/15 bg-white/12 p-4 text-sm leading-6 text-white/82">
                {usage ? (
                  <>
                    Free uses left today: <span className="font-bold text-orange-100">{usage.daily_remaining}</span> / {usage.daily_limit}
                    <span className="block">KalaManch launch pool: {usage.platform_daily_remaining} / {usage.platform_daily_limit}</span>
                  </>
                ) : (
                  'Login as an artist or collaborator to use free beta Event Planner tools.'
                )}
              </div>
              <Link to="/ai-studio" className="mt-6 inline-flex text-sm font-bold text-orange-100 hover:text-white">
                Need captions, bios, or reel scripts? Open AI Studio
              </Link>
            </div>

            <div className="p-5 sm:p-8">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {Object.values(modes).map((item) => (
                  (() => {
                    const Icon = item.icon || ClipboardList;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => chooseMode(item.id)}
                        className={`flex h-full min-h-[184px] flex-col rounded-2xl border p-4 text-left transition ${
                          modeId === item.id
                            ? 'border-[#FF6F00] bg-orange-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-orange-200'
                        }`}
                      >
                        <span className={`mb-3 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
                          modeId === item.id ? 'bg-[#FF6F00] text-white' : 'bg-slate-100 text-[#4A148C]'
                        }`}>
                          <Icon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <p className="text-[11px] font-black uppercase leading-4 tracking-[0.16em] text-[#FF6F00]">{item.eyebrow}</p>
                        <h2 className="mt-2 min-h-[2.5rem] text-lg font-black leading-tight text-slate-950">{item.title}</h2>
                        <p className="mt-2 text-sm leading-5 text-slate-600">{item.summary || item.helper}</p>
                      </button>
                    );
                  })()
                ))}
              </div>

              <div id="planner-form" className="scroll-mt-28 mt-7">
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4A148C]">{mode.eyebrow}</p>
                <h2 className="mt-2 text-3xl font-black text-slate-950">{mode.title}</h2>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <input value={organizerName} onChange={(event) => setOrganizerName(event.target.value)} placeholder="Organizer name" className="rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100" />
                <input value={eventType} onChange={(event) => setEventType(event.target.value)} placeholder={modeId === 'team' ? 'Team / project type' : modeId === 'events' ? 'Artist type / event keyword' : 'Event type'} className="rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100" />
                <input value={city} onChange={(event) => setCity(event.target.value)} placeholder="City / state" className="rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100" />
                <input value={date} onChange={(event) => setDate(event.target.value)} placeholder="Date / timeline" className="rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100" />
                <input value={audience} onChange={(event) => setAudience(event.target.value)} placeholder="Audience size" className="rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100" />
                <input value={budget} onChange={(event) => setBudget(event.target.value)} placeholder="Budget range" className="rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100" />
                <select value={language} onChange={(event) => setLanguage(event.target.value)} className="rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100 sm:col-span-2">
                  {languageOptions.map((option) => <option key={option}>{option}</option>)}
                </select>
              </div>

              <textarea
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                className="mt-4 min-h-[150px] w-full rounded-2xl border border-gray-200 px-4 py-3 text-base outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
                placeholder={mode.detailsPlaceholder}
              />

              {error && <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}

              {!authLoading && !isAuthenticated && (
                <div className="mt-4 rounded-2xl border border-purple-100 bg-purple-50 px-4 py-4">
                  <p className="text-sm font-semibold text-gray-700">Event Planner is available only for registered KalaManch artists and collaborators.</p>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <Link to="/my-account" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6F00] px-5 py-3 text-center text-sm font-bold text-white">
                      <Search className="h-4 w-4" aria-hidden="true" />
                      Artist Login
                    </Link>
                    <Link to="/collaborator/account" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#4A148C] px-5 py-3 text-center text-sm font-bold text-[#4A148C]">
                      <UsersRound className="h-4 w-4" aria-hidden="true" />
                      Collaborator Login
                    </Link>
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button type="button" onClick={generate} disabled={loading || authLoading || !isAuthenticated || !hasUsage} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-[#E65100] disabled:cursor-not-allowed disabled:opacity-60">
                  <ClipboardList className="h-4 w-4" aria-hidden="true" />
                  {loading ? 'Working...' : mode.button}
                </button>
                <button type="button" onClick={copyResult} disabled={!result} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-[#FF6F00] px-6 py-3 text-sm font-bold text-[#FF6F00] transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-50">
                  <Copy className="h-4 w-4" aria-hidden="true" />
                  {copied ? 'Copied' : 'Copy Result'}
                </button>
                {modeId === 'team' && result && (
                  <button type="button" onClick={saveCreativeTeam} className="rounded-full border-2 border-emerald-500 px-6 py-3 text-sm font-bold text-emerald-700 transition hover:bg-emerald-50">
                    Save Team
                  </button>
                )}
              </div>
              {(teamSaveStatus || referralStatus) && (
                <div className="mt-4 space-y-1 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-900">
                  {teamSaveStatus && <p>{teamSaveStatus}</p>}
                  {referralStatus && <p>{referralStatus}</p>}
                </div>
              )}

              {result && (
                <article className="mt-8 rounded-3xl border border-purple-100 bg-purple-50/50 p-5">
                  <h3 className="text-lg font-bold text-gray-950">
                    {modeId === 'finder' ? 'Artist Discovery Plan' : modeId === 'manager' ? 'Event Management Plan' : modeId === 'events' ? 'Public Event Opportunity Search' : 'Creative Team Plan'}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    {modeId === 'finder'
                      ? 'Use this to search the right artist types and message them with clearer requirements.'
                      : modeId === 'manager'
                        ? 'Use this as a first event draft for lineup, timing, cost assumptions, and operations.'
                        : modeId === 'events'
                          ? 'Use this to shortlist live events, venue leads, and public search links before pitching your KalaManch profile.'
                          : 'Use this as a reusable team structure with roles, responsibilities, and invite copy.'}
                  </p>
                  <EventResult result={result} />
                  {modeId === 'events' && opportunities && (
                    <div className="mt-7 rounded-3xl border border-emerald-100 bg-white p-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Live opportunity cards</p>
                          <h4 className="mt-1 text-xl font-black text-slate-950">Events, venues, and search leads</h4>
                        </div>
                        <span className="w-fit rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                          {(opportunities.events || []).length > 0 ? 'Live results' : 'Search fallback'}
                        </span>
                      </div>
                      {(opportunities.events || []).length > 0 && (
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          {opportunities.events.slice(0, 8).map((event, index) => (
                            <article key={`${event.title}-${index}`} className="rounded-2xl border border-purple-100 bg-purple-50/60 p-4">
                              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#4A148C]">{publicEventSourceLabel(event.source)}</p>
                              <h5 className="mt-2 text-lg font-black text-slate-950">{event.title || 'Public event'}</h5>
                              {[event.date, event.venue, event.address].filter(Boolean).length > 0 && (
                                <p className="mt-2 text-sm leading-6 text-slate-600">{[event.date, event.venue, event.address].filter(Boolean).join(' | ')}</p>
                              )}
                              {event.url && (
                                <a href={event.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex rounded-full bg-white px-3 py-1.5 text-xs font-black text-[#4A148C]">
                                  Open event
                                </a>
                              )}
                            </article>
                          ))}
                        </div>
                      )}
                      {(opportunities.venues || []).length > 0 && (
                        <div className="mt-5">
                          <p className="text-sm font-black text-slate-950">Venue leads to approach</p>
                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            {opportunities.venues.slice(0, 6).map((venue, index) => (
                              <article key={`${venue.title}-${index}`} className="rounded-2xl border border-orange-100 bg-orange-50/70 p-4">
                                <h5 className="font-black text-slate-950">{venue.title}</h5>
                                <p className="mt-1 text-sm leading-6 text-slate-600">{[venue.type, venue.address, venue.phone].filter(Boolean).join(' | ')}</p>
                                {venue.url && <a href={venue.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex text-sm font-bold text-[#FF6F00]">Open venue</a>}
                              </article>
                            ))}
                          </div>
                        </div>
                      )}
                      {(opportunities.search_links || []).length > 0 && (
                        <div className="mt-5 flex flex-wrap gap-2">
                          {opportunities.search_links.map((link) => (
                            <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="rounded-full border border-orange-200 px-4 py-2 text-xs font-black text-[#FF6F00] hover:bg-orange-50">
                              {link.label || 'Open search'}
                            </a>
                          ))}
                        </div>
                      )}
                      {(opportunities.events || []).length === 0 && (opportunities.venues || []).length === 0 && (
                        <div className="mt-4 rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold leading-6 text-[#9A3412]">
                          No direct listing appeared for this exact search yet. Try a broader keyword like concert, open mic, cultural festival, or a nearby metro city.
                        </div>
                      )}
                    </div>
                  )}
                  {modeId === 'finder' && (
                    <div className="mt-7 rounded-3xl border border-orange-100 bg-white p-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF6F00]">KalaManch matches</p>
                          <h4 className="mt-1 text-xl font-black text-slate-950">Artists matching this event</h4>
                        </div>
                        <Link
                          to={`/artists?${city ? `q=${encodeURIComponent(city)}` : ''}`}
                          className="text-sm font-bold text-[#FF6F00] hover:text-[#E65100]"
                        >
                          Browse all artists
                        </Link>
                      </div>
                      {matchedArtists.length > 0 ? (
                        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {matchedArtists.map((match) => (
                            <div key={match.id} className="rounded-[2rem] bg-white">
                              <RegisteredArtistCard artist={match} />
                              <div className="px-4 pb-4">
                                <button
                                  type="button"
                                  onClick={() => referArtist(match)}
                                  className="w-full rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50"
                                >
                                  Refer this artist
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-4 rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-900">
                          No direct artist matches found yet. Try adding the art forms needed, city, or keywords like singer, anchor, tabla, poet, dancer.
                        </p>
                      )}
                    </div>
                  )}
                  {modeId === 'finder' && matchedArtists.length > 0 && (
                    <p className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-900">
                      Tip: open a matched artist profile, then send a PMB enquiry with the generated contact message.
                    </p>
                  )}
                  {modeId === 'team' && savedTeams.length > 0 && (
                    <div className="mt-7 rounded-3xl border border-emerald-100 bg-white p-4">
                      <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Saved teams</p>
                      <h4 className="mt-1 text-xl font-black text-slate-950">Reusable creative teams</h4>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {savedTeams.slice(0, 4).map((team) => (
                          <div key={team.id} className="rounded-2xl bg-emerald-50 p-4">
                            <p className="font-bold text-slate-950">{team.title || 'Creative Team'}</p>
                            <p className="mt-1 text-sm text-emerald-900">{[team.event_type, team.city].filter(Boolean).join(' | ') || 'Reusable team plan'}</p>
                            {Array.isArray(team.roles) && team.roles.length > 0 && (
                              <p className="mt-2 text-xs font-bold text-emerald-700">Roles: {team.roles.slice(0, 6).join(', ')}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </article>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
